import type { MinimalUserDto } from '@issuemanager/api-client';
import {
  ExtendedAutocomplete,
  sortOptionsBySelected,
} from '@issuemanager/apps/client/components';
import {
  MAX_CHARACTERS_TO_SEARCH,
  MIN_CHARACTERS_TO_SEARCH,
  truncateSearchText,
} from '@issuemanager/apps/client/utils';
import { useInfiniteUsers } from '@issuemanager/client/features/users';
import { useState, type JSX } from 'react';
import type { MultipleSelectorProps } from '../types';
import { useMultipleSelectorProps } from '../use-multiple-selector';
import { mergePaginatedDataAndValue } from '../utils';

export interface UserMultipleSelectorProps<
  T extends MinimalUserDto = MinimalUserDto,
> extends MultipleSelectorProps<T[], T> {
  issueTypeId?: string;
  issueSubtypeId?: string;
}

export function UserMultipleSelector<T extends MinimalUserDto = MinimalUserDto>(
  props: UserMultipleSelectorProps<T>,
): JSX.Element {
  const {
    issueTypeId,
    issueSubtypeId,
    value,
    onChange,
    enabled: _enabled,
    ...rest
  } = props;

  const [filterByText, setFilterByText] = useState<string>('');
  const [partialValue, setPartialValue] = useState<T[]>(value ?? []);
  const usersQuery = useInfiniteUsers({
    filter: {
      searchTerm: filterByText,
      issueType: issueTypeId,
      issueSubtype: issueSubtypeId,
    },
    enabled: filterByText.length >= MIN_CHARACTERS_TO_SEARCH,
    handleError: true,
  });

  const options = mergePaginatedDataAndValue(
    usersQuery.data?.pages.map((c) => c.data) ?? [],
    value ?? [],
  ) as T[];

  const autocompleteProps = useMultipleSelectorProps<T>({
    options,
    setInputValue: (text) => setFilterByText(truncateSearchText(text)),
    query: usersQuery,
    value,
    setValue: onChange,
    partialValue,
    setPartialValue,
  });

  return (
    <ExtendedAutocomplete
      {...autocompleteProps}
      options={sortOptionsBySelected(
        options,
        partialValue,
        autocompleteProps.isOptionEqualToValue,
      )}
      getOptionLabel={(option) => `${option.name} <${option.email}>`}
      {...rest}
      maxLength={MAX_CHARACTERS_TO_SEARCH}
    />
  );
}
